import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Network, ConnectionStatus } from '@capacitor/network';

// Platform detection — wrapped in try/catch so a broken Capacitor bridge
// never prevents the app from rendering.
export const isNative = (() => {
  try {
    if (typeof navigator !== "undefined" && /Capacitor/i.test(navigator.userAgent || "")) {
      return true;
    }
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
})();
export const isIOS = (() => { try { return Capacitor.getPlatform() === 'ios'; } catch { return false; } })();
export const isAndroid = (() => { try { return Capacitor.getPlatform() === 'android'; } catch { return false; } })();
export const isWeb = (() => { try { return Capacitor.getPlatform() === 'web'; } catch { return true; } })();

// Network status state
let networkStatus: ConnectionStatus = { connected: true, connectionType: 'wifi' };
let networkPollId: ReturnType<typeof setInterval> | null = null;

/**
 * Initialize all native capabilities
 */
export async function initializeNativeApp(): Promise<void> {
  if (!isNative) {
    console.log('[Capacitor] Running in web mode');
    return;
  }

  console.log('[Capacitor] Initializing native app...');

  // Add native platform class to HTML for CSS targeting
  document.documentElement.classList.add('native-platform');

  try {
    // Initialize status bar
    await initStatusBar();
    
    // Initialize keyboard
    await initKeyboard();
    
    // Initialize network monitoring
    await initNetwork();
    
    // Lock orientation to portrait (app-wide policy — nothing unlocks it).
    const { lockPortrait, ensurePortraitLock } = await import("@/lib/screenOrientation");
    await lockPortrait();

    // Re-assert on resume so the lock can never be left released by a
    // backgrounded/interrupted native flow (camera, share sheet, OS dialog).
    App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void ensurePortraitLock();
    });

    
    // Hide splash screen after initialization
    await SplashScreen.hide();
    
    // Re-assert status bar visibility AFTER splash hides
    // (SplashScreen.hide() can reset status bar state on some iOS versions)
    await StatusBar.show().catch(() => {});
    await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    
    console.log('[Capacitor] Native app initialized successfully');
  } catch (error) {
    console.error('[Capacitor] Error initializing native app:', error);
    // Ensure splash screen is hidden even on error
    await SplashScreen.hide().catch(() => {});
  }
}

/**
 * Configure status bar for native look
 */
async function initStatusBar(): Promise<void> {
  if (!isNative) return;

  try {
    // Ensure status bar is always visible (splash may have hidden it)
    await StatusBar.show();
    
    // Dark style = dark/black text+icons on light background (correct for white BG)
    await StatusBar.setStyle({ style: Style.Dark });
    
    // Don't overlay web view — respect safe area insets naturally
    await StatusBar.setOverlaysWebView({ overlay: false });
    
    if (isAndroid) {
      await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
    }
    
    console.log('[StatusBar] Configured: visible, dark style (black text), no overlay');
  } catch (error) {
    console.error('[StatusBar] Error:', error);
  }
}

/**
 * Force status bar to dark content style.
 * Call this after screen transitions to ensure no override.
 */
export async function forceStatusBarDark(): Promise<void> {
  if (!isNative) return;
  try {
    await StatusBar.show();
    await StatusBar.setStyle({ style: Style.Dark });
  } catch {}
}

/**
 * Configure keyboard behavior
 */
async function initKeyboard(): Promise<void> {
  if (!isNative) return;

  try {
    // iOS: 'native' resize keeps focused inputs above the keyboard without
    // double-insetting against ios.contentInset: 'never'.
    // Android: body resize remains correct for full-screen WebView layouts.
    await Keyboard.setResizeMode({ mode: isIOS ? KeyboardResize.Native : KeyboardResize.Body });
    
    // Scroll to active input on keyboard show
    await Keyboard.setScroll({ isDisabled: false });
    
    if (isIOS) {
      // Set accessory bar visibility (done/cancel buttons)
      await Keyboard.setAccessoryBarVisible({ isVisible: true });
    }
  } catch (error) {
    console.error('[Keyboard] Error:', error);
  }
}

/**
 * Initialize network monitoring
 */
async function initNetwork(): Promise<void> {
  try {
    networkStatus = await Network.getStatus();

    Network.addListener('networkStatusChange', (status) => {
      applyNetworkStatus(status, 'plugin-event');
    });

    // ── Reconnect safety net ──────────────────────────────────────────────
    // Every offline drain (sync queue, upload queue, completion workflow) is
    // gated on the cached status below. On iOS the plugin's
    // `networkStatusChange` callback is NOT guaranteed to reach a foreground
    // WKWebView after Airplane Mode is switched off — when it is missed the
    // cache stays `connected:false` forever and every queue remains dormant
    // until the app is killed and `Network.getStatus()` is read again. That
    // is exactly the reported "reconnect does nothing until I restart".
    // Polling the real status makes reconnect self-healing: whichever of the
    // two observes it first wins, and the poll never fires a duplicate event.
    if (networkPollId === null) {
      networkPollId = setInterval(() => { void refreshNetworkStatus('poll'); }, 5000);
    }
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void refreshNetworkStatus('resume');
    });
  } catch (error) {
    console.error('[Network] Error:', error);
  }
}

function applyNetworkStatus(status: ConnectionStatus, source: string): void {
  const changed =
    status.connected !== networkStatus.connected ||
    status.connectionType !== networkStatus.connectionType;
  networkStatus = status;
  if (!changed) return;
  console.log(`[Network] Status changed (${source}):`, status);
  window.dispatchEvent(new CustomEvent('network-status-change', { detail: { ...status, source } }));
}

/**
 * Re-read the authoritative native network status and publish any change.
 * Safe to call anywhere; a no-op on web.
 */
export async function refreshNetworkStatus(source = 'manual'): Promise<ConnectionStatus> {
  if (!isNative) return networkStatus;
  try {
    const status = await Network.getStatus();
    applyNetworkStatus(status, source);
  } catch {
    /* plugin unavailable — keep the last known status */
  }
  return networkStatus;
}

/**
 * Get current network status
 */
export function getNetworkStatus(): ConnectionStatus {
  return networkStatus;
}


/**
 * Check if device is online
 */
export function isOnline(): boolean {
  return networkStatus.connected;
}

/**
 * Set up deep link handler for OAuth callbacks
 */
export function setupDeepLinks(onDeepLink: (url: string) => void): () => void {
  if (!isNative) return () => {};

  let listenerHandle: { remove: () => void } | null = null;

  App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    console.log('[DeepLink] Received:', event.url);
    onDeepLink(event.url);
  }).then((handle) => {
    listenerHandle = handle;
  });

  return () => {
    listenerHandle?.remove();
  };
}

/**
 * Haptic feedback utilities
 */
export const haptics = {
  /**
   * Light impact feedback (button taps)
   */
  async light(): Promise<void> {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (e) {
      // Silently fail on unsupported devices
    }
  },

  /**
   * Medium impact feedback (selections)
   */
  async medium(): Promise<void> {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (e) {}
  },

  /**
   * Heavy impact feedback (important actions)
   */
  async heavy(): Promise<void> {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (e) {}
  },

  /**
   * Success notification feedback
   */
  async success(): Promise<void> {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch (e) {}
  },

  /**
   * Warning notification feedback
   */
  async warning(): Promise<void> {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Warning });
    } catch (e) {}
  },

  /**
   * Error notification feedback
   */
  async error(): Promise<void> {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Error });
    } catch (e) {}
  },

  /**
   * Selection changed feedback
   */
  async selection(): Promise<void> {
    if (!isNative) return;
    try {
      await Haptics.selectionChanged();
    } catch (e) {}
  },
};

/**
 * Keyboard utilities
 */
export const keyboard = {
  async show(): Promise<void> {
    if (!isNative) return;
    try {
      await Keyboard.show();
    } catch (e) {}
  },

  async hide(): Promise<void> {
    if (!isNative) return;
    try {
      await Keyboard.hide();
    } catch (e) {}
  },
};
